<template>
    <KCard
        :as="as"
        variant="surface"
        :class="block()"
    >
        <div
            v-if="$slots.media || $slots.role || role || $slots.name || name"
            :class="element('header')"
        >
            <KFrame
                v-if="$slots.media"
                :class="element('media')"
                :ratio="1"
                rounded
            >
                <slot name="media" />
            </KFrame>

            <div
                v-if="$slots.role || role || $slots.name || name"
                :class="element('heading')"
            >
                <KEyebrow
                    v-if="$slots.role || role"
                    variant="pill"
                    :class="element('role')"
                >
                    <slot name="role">
                        {{ role }}
                    </slot>
                </KEyebrow>

                <component
                    :is="nameAs"
                    v-if="$slots.name || name"
                    class="k-heading-4"
                    :class="element('name')"
                >
                    <slot name="name">
                        {{ name }}
                    </slot>
                </component>
            </div>
        </div>

        <div
            v-if="$slots.default || text"
            :id="textId"
            class="k-text k-prose"
            :class="[
                element('text'),
                { 'k-team-member-card__text--collapsed': collapsible && !expanded },
            ]"
        >
            <slot>{{ text }}</slot>
        </div>

        <KButton
            v-if="collapsible && ($slots.default || text)"
            variant="ghost"
            size="sm"
            :class="element('toggle')"
            :aria-controls="textId"
            :aria-expanded="expanded"
            @click="expanded = !expanded"
        >
            {{ expanded ? collapseLabel : expandLabel }}
        </KButton>
    </KCard>
</template>

<script setup lang="ts">
import type { KTeamMemberCardProps } from './types'
import useBem from '@yeeroen/use-bem'

import { ref, useId } from 'vue'
import KButton from '../button/k-button.vue'
import KCard from '../card/k-card.vue'
import KEyebrow from '../eyebrow/k-eyebrow.vue'
import KFrame from '../frame/k-frame.vue'

defineOptions({ name: 'KTeamMemberCard' })

withDefaults(defineProps<KTeamMemberCardProps>(), {
    as: 'article',
    nameAs: 'h3',
    collapsible: false,
    expandLabel: 'Read more',
    collapseLabel: 'Show less',
})

defineSlots<{
    default?: () => unknown
    media?: () => unknown
    name?: () => unknown
    role?: () => unknown
}>()

const { block, element } = useBem('k-team-member-card')
const expanded = ref(false)
const textId = useId()
</script>

<style lang="scss">
@use './k-team-member-card.scss';
</style>
